import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RetrospectiveTemplate } from '../types';
import toast from 'react-hot-toast';
import { useApiClient } from '../utils/authenticatedApiClient';
import { API_ENDPOINTS } from '../utils/apiClient';
import { AuthButton } from '../components/AuthButton';

const TemplateSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiClient = useApiClient();
  const [templates, setTemplates] = useState<RetrospectiveTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [boardTitle, setBoardTitle] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  useEffect(() => {
    fetchTemplates();
    
    // Check for team parameters from URL
    const teamParam = searchParams.get('team');
    const teamIdParam = searchParams.get('teamId');
    
    if (teamParam) {
      setTeamName(teamParam);
    }
    
    if (teamIdParam) {
      setTeamId(teamIdParam);
    }
  }, [searchParams]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get<RetrospectiveTemplate[]>(API_ENDPOINTS.TEMPLATES);
      if (response.data) {
        setTemplates(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!selectedTemplate || !boardTitle.trim()) {
      toast.error('Please select a template and enter a board title');
      return;
    }

    try {
      const response = await apiClient.post(API_ENDPOINTS.BOARDS, {
        templateId: selectedTemplate,
        title: boardTitle.trim(),
        teamName: teamName.trim() || undefined,
        teamId: teamId || undefined,
      });

      if (response.data) {
        toast.success('Board created successfully!');
        navigate(`/board/${response.data.id}`);
      } else {
        throw new Error(response.error || 'Failed to create board');
      }
    } catch (error) {
      console.error('Error creating board:', error);
      toast.error('Failed to create board');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to={teamId ? "/teams" : "/"}
              className="flex items-center text-indigo-600 hover:text-indigo-700 mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              {teamId ? 'Back to Teams' : 'Back'}
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">
              {teamId ? `Create Board for ${teamName}` : 'Create New Board'}
            </h1>
          </div>
          <AuthButton />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Board Setup */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-lg sticky top-8">
              <h2 className="text-xl font-semibold mb-4">Board Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="boardTitle" className="block text-sm font-medium text-gray-700 mb-2">
                    Board Title *
                  </label>
                  <input
                    type="text"
                    id="boardTitle"
                    value={boardTitle}
                    onChange={(e) => setBoardTitle(e.target.value)}
                    placeholder="Sprint 23 Retrospective"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label htmlFor="teamName" className="block text-sm font-medium text-gray-700 mb-2">
                    Team Name {teamId ? '' : '(optional)'}
                  </label>
                  <input
                    type="text"
                    id="teamName"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Alpha Team"
                    disabled={!!teamId}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      teamId ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                  {teamId && (
                    <p className="text-sm text-gray-500 mt-1">
                      Creating board for existing team
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleCreateBoard}
                disabled={!selectedTemplate || !boardTitle.trim()}
                className="w-full mt-6 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Create Board
              </button>
            </div>
          </div>

          {/* Template Selection */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold mb-6">Choose a Template</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`template-card bg-white rounded-xl p-6 shadow-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedTemplate === template.id
                      ? 'border-indigo-500 ring-2 ring-indigo-200'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {template.description}
                      </p>
                    </div>
                    <ChevronRight 
                      className={`h-5 w-5 transition-colors ${
                        selectedTemplate === template.id ? 'text-indigo-500' : 'text-gray-400'
                      }`} 
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Columns:</h4>
                    <div className="flex flex-wrap gap-2">
                      {template.columns.map((column) => (
                        <span
                          key={column.id}
                          className={`px-3 py-1 text-xs font-medium rounded-full ${column.color} text-gray-800`}
                        >
                          {column.title}
                        </span>
                      ))}
                    </div>
                  </div>

                  {template.isDefault && (
                    <div className="mt-3 flex items-center">
                      <Lightbulb className="h-4 w-4 text-yellow-500 mr-1" />
                      <span className="text-xs text-yellow-600 font-medium">Recommended</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelectionPage;